import { QueryFunction, QueryKey, useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { useRef } from "react";
import { useCancellableRequest } from "./use-cancellable-request.hook";

// Base error interface to enforce error structure
interface ErrorWithMessage {
  message?: string;
  name?: string;
}

/**
 * A wrapper around React Query's useQuery that properly handles request cancellation
 * to prevent race conditions and duplicate requests
 */
export function useCancellableQuery<
  TQueryFnData = unknown,
  TError extends ErrorWithMessage = ErrorWithMessage,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey
>(
  queryKey: TQueryKey,
  queryFn: QueryFunction<TQueryFnData, TQueryKey>,
  options?: Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, "queryKey" | "queryFn">
): UseQueryResult<TData, TError> & { cancelRequest: () => void } {
  const { getSignal, cancelRequest } = useCancellableRequest();
  const currentRequestIdRef = useRef<string>("");

  const wrappedQueryFn: QueryFunction<TQueryFnData, TQueryKey> = async (context) => {
    // Generate a unique ID for this request
    const requestId = `${Date.now()}-${Math.random()}`;
    currentRequestIdRef.current = requestId;

    // Cancel any previous request first
    cancelRequest();

    // Create a signal for this request
    const signal = getSignal(false);

    return new Promise<TQueryFnData>((resolve, reject) => {
      // Call the original queryFn with the abort signal added to the context
      Promise.resolve(
        queryFn({
          ...context,
          signal,
        })
      )
        .then((response) => {
          if (currentRequestIdRef.current === requestId) {
            resolve(response);
          } else {
            reject(new Error("Request superseded"));
          }
        })
        .catch((err) => {
          if (currentRequestIdRef.current === requestId) {
            reject(err);
          } else {
            reject(new Error("Request superseded"));
          }
        });
    });
  };

  // Configure the retry behavior to avoid retrying cancelled requests
  const enhancedOptions = {
    ...options,
    retry: (failureCount: number, error: TError) => {
      if (error?.message === "Request superseded" || error?.name === "AbortError") {
        return false;
      }
      if (options?.retry) {
        return typeof options.retry === "function" ? options.retry(failureCount, error) : options.retry;
      }
      return failureCount < 3;
    },
  } as UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;

  const queryResult = useQuery<TQueryFnData, TError, TData, TQueryKey>({
    queryFn: wrappedQueryFn,
    ...enhancedOptions,
    queryKey,
  });

  return {
    ...queryResult,
    cancelRequest: (): void => {
      cancelRequest();
      currentRequestIdRef.current = "";
    },
  };
}
